import { Product, KeywordWithIntent } from '../types';

/**
 * Represents an intent with its overlap score
 */
export interface IntentOverlapScore {
  intent: string;
  score: number;
  keywordCount: number;
  overlapDetails: {
    [otherIntent: string]: number; // How many keywords overlap with each other intent
  };
}

/**
 * Check if an intent should be excluded from auto-selection
 * Intents like "none", "other", "extra" are considered low-priority catch-alls
 */
export function isExcludedIntent(intent: string): boolean {
  const normalizedIntent = intent.toLowerCase().trim();
  return normalizedIntent === 'none' || 
         normalizedIntent === 'other' || 
         normalizedIntent === 'extra';
}

/**
 * Calculate which intents have the most keyword overlap with other intent categories.
 * An intent gets a higher score if its keywords appear in multiple different intent categories.
 * This indicates broader coverage across the keyword space.
 * 
 * @param product The product to analyze
 * @returns Array of intents sorted by overlap score (highest first)
 */
export function calculateIntentOverlap(product: Product): IntentOverlapScore[] {
  // Build a map of keywords to the intents they appear in
  const keywordToIntents = new Map<string, Set<string>>();
  
  product.keywords.forEach((keyword) => {
    // Normalize keyword text for comparison (case-insensitive)
    const normalizedText = keyword.text.toLowerCase().trim();
    
    if (!keywordToIntents.has(normalizedText)) {
      keywordToIntents.set(normalizedText, new Set());
    }
    keywordToIntents.get(normalizedText)!.add(keyword.intent);
  });

  // Group keywords by intent
  const intentToKeywords = new Map<string, KeywordWithIntent[]>();
  product.keywords.forEach((keyword) => {
    if (!intentToKeywords.has(keyword.intent)) {
      intentToKeywords.set(keyword.intent, []);
    }
    intentToKeywords.get(keyword.intent)!.push(keyword);
  });

  // Calculate overlap scores for each intent
  const intentScores: IntentOverlapScore[] = [];
  
  intentToKeywords.forEach((keywords, intent) => {
    const overlapDetails: { [otherIntent: string]: number } = {};
    let totalOverlapScore = 0;
    
    // For each keyword in this intent, check how many other intents it appears in
    keywords.forEach((keyword) => {
      const normalizedText = keyword.text.toLowerCase().trim();
      const intentsForKeyword = keywordToIntents.get(normalizedText);
      
      if (intentsForKeyword && intentsForKeyword.size > 1) {
        // This keyword appears in multiple intents
        // Count overlaps with each other intent
        intentsForKeyword.forEach((otherIntent) => {
          if (otherIntent !== intent) {
            overlapDetails[otherIntent] = (overlapDetails[otherIntent] || 0) + 1;
            totalOverlapScore += 1;
          }
        });
      }
    });

    intentScores.push({
      intent,
      score: totalOverlapScore,
      keywordCount: keywords.length,
      overlapDetails
    });
  });

  // Sort by overlap score (descending), then by keyword count (descending) as tiebreaker
  intentScores.sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score;
    }
    return b.keywordCount - a.keywordCount;
  });

  return intentScores;
}

/**
 * Select the top N intents using a greedy maximum coverage algorithm
 * 
 * Algorithm:
 * 1. First intent: Select the intent with the most overlap across other categories (broadest coverage)
 * 2. Subsequent intents: Greedily select intents that add the most NEW keywords not yet covered
 * 
 * @param product The product to analyze
 * @param n Number of top intents to select
 * @returns Array of selected intent names
 */
export function selectTopIntentsByOverlap(product: Product, n: number): string[] {
  if (n <= 0) return [];
  
  const intentScores = calculateIntentOverlap(product);
  if (intentScores.length === 0) return [];
  
  // Filter out excluded intents (none, other, extra) from consideration
  const eligibleIntentScores = intentScores.filter(score => !isExcludedIntent(score.intent));
  if (eligibleIntentScores.length === 0) return [];
  
  // Limit n to the number of available eligible intents
  const actualN = Math.min(n, eligibleIntentScores.length);
  
  // Build a map of intent -> set of keywords
  const intentToKeywords = new Map<string, Set<string>>();
  product.keywords.forEach((keyword) => {
    const normalizedText = keyword.text.toLowerCase().trim();
    if (!intentToKeywords.has(keyword.intent)) {
      intentToKeywords.set(keyword.intent, new Set());
    }
    intentToKeywords.get(keyword.intent)!.add(normalizedText);
  });
  
  const selectedIntents: string[] = [];
  const coveredKeywords = new Set<string>();
  
  // Step 1: Select the first intent with the highest overlap score
  const firstIntent = eligibleIntentScores[0].intent;
  selectedIntents.push(firstIntent);
  
  // Add all keywords from the first intent to covered set
  const firstIntentKeywords = intentToKeywords.get(firstIntent);
  if (firstIntentKeywords) {
    firstIntentKeywords.forEach(keyword => coveredKeywords.add(keyword));
  }
  
  // Step 2-N: Greedily select remaining intents that maximize new keyword coverage
  for (let i = 1; i < actualN; i++) {
    let bestIntent: string | null = null;
    let maxNewKeywords = 0;
    
    // Check all eligible intents not yet selected
    intentToKeywords.forEach((keywords, intent) => {
      if (selectedIntents.includes(intent)) return; // Skip already selected
      if (isExcludedIntent(intent)) return; // Skip excluded intents
      
      // Count how many NEW keywords this intent would add
      let newKeywordCount = 0;
      keywords.forEach(keyword => {
        if (!coveredKeywords.has(keyword)) {
          newKeywordCount++;
        }
      });
      
      // Keep track of the best option
      if (newKeywordCount > maxNewKeywords) {
        maxNewKeywords = newKeywordCount;
        bestIntent = intent;
      }
    });
    
    // If we found a best intent, add it
    if (bestIntent) {
      selectedIntents.push(bestIntent);
      
      // Add its keywords to covered set
      const bestIntentKeywords = intentToKeywords.get(bestIntent);
      if (bestIntentKeywords) {
        bestIntentKeywords.forEach(keyword => coveredKeywords.add(keyword));
      }
    } else {
      // No more intents to add (all remaining intents have zero new keywords)
      break;
    }
  }
  
  return selectedIntents;
}

/**
 * Get all keywords that belong to specific intents
 * 
 * @param product The product to get keywords from
 * @param intents Array of intent names
 * @returns Array of keyword texts
 */
export function getKeywordsForIntents(product: Product, intents: string[]): string[] {
  const intentSet = new Set(intents);
  return product.keywords
    .filter(keyword => intentSet.has(keyword.intent))
    .map(keyword => keyword.text);
}

